import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { Option, OptionGroupMultiSelect } from 'src/shared/models/option';
import { PopoverMultiSelectComponent } from "../popover-multi-select/popover-multi-select.component";
import { PillOption, Role, RoleOptions } from 'src/shared/models/fga/role';
import { InfoCircleComponent } from '../info-circle/info-circle.component';
import { PillComponent } from '../pill/pill.component';

@Component({
  selector: 'motn-pill-multiselect',
  templateUrl: './pill-multiselect.component.html',
  styleUrls: ['./pill-multiselect.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PopoverMultiSelectComponent, InfoCircleComponent, PillComponent],
})
export class PillMultiselectComponent<K = string> implements OnInit, OnDestroy, OnChanges {
  @Input() items: PillOption<Role>[] = [];
  @Input() selected: PillOption<Role>[] = [];
  @Input() openMenu$: Observable<void>;
  @Input() requireAtLeastOne = false;
  @Input() errorMsgInline = false;
  // This is currently a fixed message but could be converted to an @Input() parameter
  // if this component is used in contexts requiring different error messages
  readonly errorMsg = 'At least one role must be selected.';
  readonly errorInfoMsg = 'To remove all access from the user, select the menu to the left of the user\'s name and then select "Remove User".';

  @Output() add = new EventEmitter<PillOption<Role>>();
  @Output() remove = new EventEmitter<PillOption<Role>>();

  menuOpen = false;
  showError = false;
  private openMenuSub?: Subscription;

  optionGroup: OptionGroupMultiSelect;

  @ViewChild('container', { static: true }) containerRef: ElementRef;

  ngOnInit() {
    if (this.openMenu$) {
      this.openMenuSub = this.openMenu$.subscribe(() => this.openMenu());
    }
    this.selected = this.sortItems(this.selected);
    this.initOptionGroup();
  }

  ngOnDestroy() {
    this.openMenuSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selected'] && this.selected?.length > 0) {
      this.selected = this.sortItems(this.selected);
      this.initOptionGroup();
    }
  }

  private initOptionGroup() {
    this.optionGroup = new OptionGroupMultiSelect(
      this.sortItems(this.items).map(item => ({
        id: item.key,
        name: item.label,
        selected: this.isSelected(item.key),
      }))
    );
  }

  private sortItems<T extends PillOption<Role>>(items: T[]): T[] {
    return [...items].sort((a, b) => a.label.localeCompare(b.label));
  }

  private isSelected(key: Role): boolean {
    return this.selected.some(item => item.key === key);
  }

  onContainerClick(event: MouseEvent) {
    this.openMenu();
  }

  openMenu(event?: MouseEvent) {
    event?.stopPropagation();
    this.menuOpen = true;
    this.showError = false;
  }

  closeMenu() {
    this.menuOpen = false;
    this.ensureMinimumSelection();
  }

  private ensureMinimumSelection(): void {
    if (this.requireAtLeastOne && this.selected.length === 0) {
      this.showError = true;
      const defaultOption = RoleOptions.find(opt => opt.key === Role.VALUE_READER);
      if (defaultOption) {
        this.addItem(defaultOption);
      }
    }
  }

  addItem(option: PillOption<Role>) {
    if (!this.isSelected(option.key)) {
      this.selected = this.sortItems([...this.selected, option]);
      this.add.emit(option);
    }
  }

  removeItem(option: PillOption<Role>) {
    if (this.shouldPreventRemoval()) {
      this.showError = true;
      return;
    }
    
    this.selected = this.selected.filter(item => item.key !== option.key);
    this.updateOptionGroupSelection(option.key, false);
    this.remove.emit(option);
  }

  private shouldPreventRemoval(): boolean {
    return this.requireAtLeastOne && this.selected.length <= 1 && !this.menuOpen;
  }

  private updateOptionGroupSelection(key: Role, selected: boolean): void {
    const option = this.optionGroup.options.find(opt => opt.id === key);
    if (option) {
      option.selected = selected;
    }
  }

  onPopoverOptionClicked(option: Option) {
    const pillOption: PillOption<Role> = { key: option.id as Role, label: option.name };
    
    if (option.selected) {
      this.addItem(pillOption);
    } else {
      this.removeItem(pillOption);
    }
  }
}
